import { Box, FormControl, MenuItem, Select, Skeleton, SxProps, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ElementItemProps } from "../types";
import ElementFrame from "./ElementFrame";
import { useDevice } from "../../../../utils/DeviceProvider";
import useEditedStyles from "../../../../hooks/useEditedStyles";
import { useIntl } from "react-intl";
import { useLocale } from "../../../../locales/LocaleProvider";
import { getLabel } from "../../../../utils/utils";
import { TxtboxElement } from "./Txtbox";
import { ResetAdornment, useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export interface ComboxParameters {
  items: Array<string | { value: string; label: string | { [locale: string]: string } }>;
  custom?: { value: string; label: string | { [locale: string]: string } };
}

const Combox = ({ label: labelParam, items: itemsParam, custom, configKey, elementKey, disabled }: ElementItemProps<ComboxParameters>) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [value, setValue] = useState<string | null>(null);
  const [customValue, setCustomValue] = useState("");

  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const { formatMessage } = useIntl();
  const { locale } = useLocale();
  const { openContextMenu } = useResetContextMenu();

  const label = useMemo(() => getLabel(labelParam, locale), [labelParam, locale]);
  const items = useMemo(() => {
    const items = itemsParam.map((item, index) => ({
      value: typeof item === "string" ? `${index}` : item.value,
      label: typeof item === "string" ? item : getLabel(item.label, locale),
    }));

    if (custom) {
      items.push({ value: custom.value, label: getLabel(custom.label, locale) });
    }
    return items;
  }, [itemsParam, locale, custom]);

  const onChange = useCallback(() => {
    if (configIndex === null) {
      return;
    }
    const val = custom && value === custom.value ? customValue : value;
    currentValue[configKey].items[configIndex].value = val;
    setCurrentValue(currentValue);
  }, [setCurrentValue, currentValue, configKey, configIndex, value, custom, customValue]);

  const handleComboChange = useCallback(
    (v: string) => {
      if (custom) {
        if (v !== custom.value) {
          setCustomValue("");
        }
        setValue(v);
      } else {
        setValue(v);
      }
    },
    [custom]
  );

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex =
      currentValue[configKey]?.items?.findIndex(({ type, key }: { type: string; key?: string }) => type === "COMBOX" && (key === elementKey || `${key}` === elementKey)) ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue, elementKey]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex]?.value ?? null;
    setSaved(svd);
    const current = currentValue[configKey].items[configIndex]?.value;
    if (custom) {
      const isCustomValue = current !== custom.value && !items.map(({ value }) => value).includes(current);
      if (isCustomValue) {
        setValue(custom.value);
        setCustomValue(current);
      } else {
        setValue(current);
        setCustomValue("");
      }
    } else {
      setValue(current ?? "");
    }
  }, [active, currentValue, config, configIndex, configKey, custom, items]);

  if (value === null) {
    return (
      <ElementFrame label={label}>
        <Skeleton variant="rectangular" height={40} sx={{ width: "100%", borderRadius: 1 }} />
      </ElementFrame>
    );
  }

  if (configIndex === -1) {
    return (
      <ElementFrame label={label}>
        <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>
      </ElementFrame>
    );
  }

  return (
    <Box sx={{ display: "flex", alignSelf: "stretch", alignItems: "flex-end" }}>
      <ComboxElement
        label={label}
        items={items}
        value={value ?? ""}
        onChange={handleComboChange}
        onBlur={onChange}
        edited={(saved ?? "") !== (custom && value === custom.value ? customValue : value)}
        sx={[value !== custom?.value && { flex: 1 }]}
        disabled={disabled}
        onContextMenu={(saved || value) && saved !== value ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value`) : undefined}
      />
      {custom && value === custom.value ? (
        <TxtboxElement
          label=""
          value={customValue}
          onChange={setCustomValue}
          onBlur={onChange}
          edited={saved !== (custom && value === custom.value ? customValue : value)}
          sx={{ marginLeft: 1, flex: 1 }}
          disabled={disabled}
          onContextMenu={
            saved !== (custom && value === custom.value ? customValue : value)
              ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value`)
              : undefined
          }
        />
      ) : null}
    </Box>
  );
};

export const ComboxElement = ({
  label,
  items,
  value,
  onChange,
  sx,
  inputSx,
  disabled,
  edited,
  onBlur,
  onContextMenu,
}: {
  label: string;
  items: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  sx?: SxProps;
  inputSx?: SxProps;
  disabled?: boolean;
  edited?: boolean;
  onBlur: () => void;
  onContextMenu?: React.MouseEventHandler<HTMLElement>;
}) => {
  const editedStyle = useEditedStyles();

  return (
    <ElementFrame label={label} sx={sx} disabled={disabled}>
      <FormControl
        variant="filled"
        size="small"
        hiddenLabel
        sx={{ margin: 0, minWidth: 100, ...inputSx }}
        className={edited ? editedStyle.input : ""}
        disabled={disabled}
        fullWidth
        onContextMenu={onContextMenu}
      >
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          sx={[{ margin: 0 }, !!onContextMenu && { "& .MuiSelect-select": { pr: (theme) => `${theme.spacing(5.5)} !important` } }]}
          IconComponent={(props) => (
            <Box sx={{ position: "absolute", right: 0 }}>
              {onContextMenu ? <ResetAdornment sx={{ mr: 3.5 }} /> : undefined}
              <ArrowDropDownIcon {...props} />
            </Box>
          )}
        >
          {items.map(({ value, label }) => (
            <MenuItem key={`option-${value}`} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </ElementFrame>
  );
};

export default Combox;
